import { useQuery } from "@tanstack/react-query";
import { gql } from "graphql-request";
// eslint-disable-next-line no-restricted-imports
import { useStore } from "membership-management-portal/src/store";

import {
  PaymentMethod,
  Subscription,
  MembershipType,
  Contact,
  MembershipOfferings,
} from "../types";
import { axiosQuery } from "../utils/axiosHelpers";

export interface GetAccountResponse {
  account: {
    email: string;
    createdAt: string;
    dunningLevel: number;
    shipToContact: Contact | null;
    billToContact: Contact | null;
    membershipType?: MembershipType; // change it to correct typing
    currentSubscription: Subscription | null;
    subscriptions: Subscription[];
    paymentMethods: PaymentMethod[];
    isPaymentMethodReusableInRenew: boolean;
  };
  membershipOffering: MembershipOfferings;
}

const GET_ACCOUNT_QUERY = gql`
  query GetAccount {
    membershipOffering {
      membershipStatus {
        type
        subscriptionFound
        signupComplete
      }
      channel
      entitlement {
        type
      }
      customerId
      customerName
      products {
        name
        channel
        trial {
          length
          periodType
        }
        membership {
          periodType
          length
        }
        taxMode
        countries {
          code
          pricing {
            currency
            amount
          }
        }
      }
    }
    account {
      email
      dunningLevel
      createdAt
      shipToContact {
        firstName
        lastName
        address1
        address2
        city
        country
        postalCode
        state
        personalEmail
        manuallyVerified
      }
      billToContact {
        firstName
        lastName
        address1
        address2
        city
        country
        postalCode
        state
        personalEmail
        manuallyVerified
      }
      paymentMethods {
        ... on ApiCreditCardPaymentMethod {
          type
          isDefault
          status
          cardNumber
          isExpired
        }
        ... on ApiPayPalPaymentMethod {
          type
          isDefault
          status
        }
      }
      membershipType
      currentSubscription {
        id
        channel
        subscriptionType
        renewalState
        subscriptionState
        nextBillingDate
        nextPaymentDate
        endDate
        startDate
        pendingCancellation
        recurringFee {
          amount
          billingPeriod
          currency
        }
        alternativeBillingPeriods {
          amount
          billingPeriod
          currency
        }
        prepaidPeriods {
          current {
            start
            end
            lengthInMonths
          }
          future {
            start
            end
            lengthInMonths
          }
        }
      }
      subscriptions {
        id
        channel
        subscriptionType
        renewalState
        subscriptionState
        nextBillingDate
        endDate
        startDate
        pendingCancellation
        recurringFee {
          amount
          billingPeriod
          currency
        }
        alternativeBillingPeriods {
          amount
          billingPeriod
          currency
        }
      }
      isPaymentMethodReusableInRenew
    }
  }
`;

// loginToken is the accessToken from Login response. We use it to tell react-query to fetch when a new user login instead of returning the cached data of previously logged-in user. We soon will use accountID when Login response is updated.(MMBER-5064)
export const useGetAccount = () => {
  const token = useStore((state) => state.loginToken);
  return useQuery<GetAccountResponse>({
    queryKey: ["getPaymentMethods", token],
    queryFn: () =>
      axiosQuery<GetAccountResponse>("GetAccount", GET_ACCOUNT_QUERY),
    retry: false,
    staleTime: Infinity,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};
